import { create } from 'zustand'

import { CLIENT_ID } from '@/api/client'
import { createMealPlan, deleteMealPlan, getMealPlan, suggestMealPlan } from '@/api/services/nutrition'
import { registerSyncListener } from '@/api/sync-events'
import type { MealPlanCreate, MealPlanRead, MealPlanSuggestion, MealPlanSuggestRequest } from '@/api/types/nutrition'

type MealPlanState = {
  // State
  currentPlan: MealPlanRead | null
  /** 最後に fetchPlan した日付（SSE 再取得用に保持） */
  currentPlanDate: string | null
  suggestions: MealPlanSuggestion[]
  isLoadingPlan: boolean
  isLoadingSuggestion: boolean
  isSaving: boolean

  // Actions
  fetchPlan: (date: string) => Promise<void>
  savePlan: (data: MealPlanCreate) => Promise<void>
  requestSuggestion: (data: MealPlanSuggestRequest) => Promise<void>
  removePlan: (planId: number) => Promise<void>
  clearSuggestion: () => void
  reset: () => void
}

export const useMealPlanStore = create<MealPlanState>((set) => ({
  currentPlan: null,
  currentPlanDate: null,
  suggestions: [],
  isLoadingPlan: false,
  isLoadingSuggestion: false,
  isSaving: false,

  fetchPlan: async (date: string) => {
    set({ isLoadingPlan: true, currentPlanDate: date })
    try {
      const plan = await getMealPlan(date)
      set({ currentPlan: plan })
    } catch {
      // 403 (full_access required) などのエラーは無視
      set({ currentPlan: null })
    } finally {
      set({ isLoadingPlan: false })
    }
  },

  savePlan: async (data: MealPlanCreate) => {
    set({ isSaving: true })
    try {
      const plan = await createMealPlan(data)
      set({ currentPlan: plan, currentPlanDate: data.planned_date })
    } finally {
      set({ isSaving: false })
    }
  },

  requestSuggestion: async (data: MealPlanSuggestRequest) => {
    set({ isLoadingSuggestion: true })
    try {
      const suggestions = await suggestMealPlan(data)
      set({ suggestions })
    } finally {
      set({ isLoadingSuggestion: false })
    }
  },

  removePlan: async (planId: number) => {
    await deleteMealPlan(planId)
    set({ currentPlan: null, suggestions: [] })
  },

  clearSuggestion: () => set({ suggestions: [] }),

  reset: () =>
    set({
      currentPlan: null,
      currentPlanDate: null,
      suggestions: [],
      isLoadingPlan: false,
      isLoadingSuggestion: false,
      isSaving: false,
    }),
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// 他端末で食事計画が更新されたら、いま表示中の日付を再取得する。
registerSyncListener((event) => {
  if (event.type !== 'meal_plan.updated') return
  if (event.origin === CLIENT_ID) return

  const { currentPlanDate, fetchPlan } = useMealPlanStore.getState()
  if (!currentPlanDate) return
  void fetchPlan(currentPlanDate)
})
